import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

/**
 * Accent palette for KPI cards, restricted to colors already used across the
 * dashboards (see AdminDashboardPage/DashboardPage) so new cards stay
 * visually consistent with the existing semantic color meanings
 * (terracotta = primary/brand, green = revenue, blue = neutral count,
 * purple = secondary count, red = overdue/alert, amber = pending/warning).
 */
const ACCENT_STYLES = {
  terracotta: { badgeBg: 'bg-terracotta-50', badgeText: 'text-terracotta-600', value: 'text-terracotta-700' },
  green: { badgeBg: 'bg-green-50 dark:bg-green-950', badgeText: 'text-green-600 dark:text-green-400', value: 'text-green-600 dark:text-green-400' },
  blue: { badgeBg: 'bg-blue-50 dark:bg-blue-950', badgeText: 'text-blue-600 dark:text-blue-400', value: 'text-blue-600 dark:text-blue-400' },
  purple: { badgeBg: 'bg-purple-50 dark:bg-purple-950', badgeText: 'text-purple-600 dark:text-purple-400', value: 'text-purple-600 dark:text-purple-400' },
  red: { badgeBg: 'bg-red-50 dark:bg-red-950', badgeText: 'text-red-600 dark:text-red-400', value: 'text-red-600 dark:text-red-400' },
  amber: { badgeBg: 'bg-amber-50 dark:bg-amber-950', badgeText: 'text-amber-600 dark:text-amber-400', value: 'text-amber-700 dark:text-amber-400' },
} as const

export type KpiAccent = keyof typeof ACCENT_STYLES

interface KpiCardProps {
  icon: LucideIcon
  label: string
  value: ReactNode
  accent: KpiAccent
  subtext?: ReactNode
}

/**
 * Shared KPI card for the seller and admin dashboards: an accent-colored
 * icon badge above a large value and label, with an optional subtext line.
 * Keeps the two dashboards visually consistent instead of each re-styling
 * its own summary cards. Vertical badge-on-top layout (icon insignia, big
 * number, label below) instead of the older icon-left/text-right row --
 * inspired by .guides/PlantillaKPIs.png's KPI row, kept in this app's own
 * terracotta/espresso/cream palette rather than the template's blue.
 */
export default function KpiCard({ icon: Icon, label, value, accent, subtext }: KpiCardProps) {
  const styles = ACCENT_STYLES[accent]
  return (
    <div className="flex flex-col items-center overflow-hidden rounded-lg border border-cream-200 bg-cream-50 p-5 text-center shadow sm:p-6">
      <span
        className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ${styles.badgeBg} ${styles.badgeText}`}
      >
        <Icon className="h-7 w-7" aria-hidden="true" />
      </span>
      <div className={`mt-3 text-2xl font-bold sm:text-3xl ${styles.value}`}>{value}</div>
      <div className="mt-1 text-sm font-medium text-espresso-500">{label}</div>
      {subtext && <div className="mt-1 text-xs text-espresso-500">{subtext}</div>}
    </div>
  )
}
